/**
 * Diagnostics card: per-month render trace for the three history charts fed
 * by the snapshot/backfill self-heal (Cash Development / jurisdiction chart,
 * Bank Balance per-account chart, Net Assets cash component).
 *
 * Answers "snapshot docs exist but the chart barely renders any history" by
 * showing, side by side, what's actually persisted in each collection vs
 * what the live chart endpoint returns for the same window -- so a gap is
 * visible by diff instead of inferred from the chart's shape.
 */
import React, { useState } from 'react';
import { Loader2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { snapshotRenderTraceAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function CollectionVsReturned({ title, collection, monthsReturned, dropped, partialMonths, entityIdsQueried }) {
  const monthsPresent = collection.months_present || [];
  const partialByMonth = {};
  (partialMonths || []).forEach((p) => { partialByMonth[p.month] = p; });
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className="text-[10px]">{title}</Badge>
        <span className="text-[11px] text-muted-foreground">
          {collection.total_docs} doc(s) persisted, {monthsPresent.length} distinct month(s),
          {' '}{monthsReturned.length} returned to the chart
        </span>
      </div>
      {entityIdsQueried && entityIdsQueried.length > 0 && (
        <div className="text-[11px] text-muted-foreground mb-1">
          Scoped query: entity_id in [{entityIdsQueried.join(', ')}]
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead>Persisted (by source)</TableHead>
            <TableHead>Returned to chart?</TableHead>
            <TableHead>If dropped or partial, why</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {monthsPresent.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-xs text-muted-foreground">No months persisted at all.</TableCell>
            </TableRow>
          )}
          {monthsPresent.map((month) => {
            const bySource = collection.by_month_source?.[month] || {};
            const returned = monthsReturned.includes(month);
            const dropReason = (dropped || []).find((d) => d.month === month);
            const partial = partialByMonth[month];
            return (
              <TableRow key={month}>
                <TableCell className="text-xs">{month}</TableCell>
                <TableCell className="text-xs">
                  {Object.entries(bySource).map(([src, count]) => `${src}: ${count}`).join(', ')}
                </TableCell>
                <TableCell className="text-xs">
                  {returned
                    ? (partial
                      ? <span className="text-amber-700">yes (partial)</span>
                      : <span className="text-green-700">yes</span>)
                    : <span className="text-amber-700">no</span>}
                </TableCell>
                <TableCell className="text-xs text-amber-700">
                  {dropReason
                    ? dropReason.reason
                    : (partial
                      ? `partial -- excludes ${(partial.excluded_account_labels || partial.excluded_accounts || []).join(', ')}`
                      : '-')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function SnapshotRenderTracePanel() {
  const [scope, setScope] = useState('entities');
  const [entityId, setEntityId] = useState('');
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState(null);

  const runTrace = async () => {
    setLoading(true);
    try {
      const { data } = await snapshotRenderTraceAPI.get({
        scope, entityId: entityId || undefined, months,
      });
      setTrace(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="text-base">Snapshot Render Trace</CardTitle>
          <CardDescription>
            Persisted history vs what the Bank Balance / Cash Development / Net Assets charts actually render, per month
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <label className="text-xs text-muted-foreground" htmlFor="srt-scope">Scope</label>
          <select
            id="srt-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="h-8 px-2 text-xs rounded-md border border-input bg-background"
            data-testid="diag-snapshot-render-trace-scope"
          >
            <option value="entities">All Entities</option>
            <option value="personal">Personal</option>
          </select>
          <label className="text-xs text-muted-foreground" htmlFor="srt-entity-id">Entity ID (optional)</label>
          <Input
            id="srt-entity-id"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            placeholder="e.g. bi_sas"
            className="h-8 w-32 text-xs"
          />
          <label className="text-xs text-muted-foreground" htmlFor="srt-months">Months</label>
          <Input
            id="srt-months"
            type="number"
            min={1}
            max={36}
            value={months}
            onChange={(e) => setMonths(Math.max(1, Math.min(36, parseInt(e.target.value, 10) || 1)))}
            className="h-8 w-20 text-xs"
          />
          <button
            onClick={runTrace}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
            data-testid="diag-snapshot-render-trace-run-btn"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
            Load trace
          </button>
        </div>

        {trace && (
          <div data-testid="diag-snapshot-render-trace-results">
            <CollectionVsReturned
              title="Jurisdiction chart (Cash Development)"
              collection={trace.jurisdiction_chart.collection}
              monthsReturned={trace.jurisdiction_chart.months_returned}
              dropped={trace.jurisdiction_chart.months_present_but_dropped}
            />
            <CollectionVsReturned
              title="Bank Balance chart"
              collection={trace.bank_balance_chart.collection}
              monthsReturned={trace.bank_balance_chart.months_returned}
              dropped={trace.bank_balance_chart.months_present_but_dropped}
              partialMonths={trace.bank_balance_chart.months_partial}
              entityIdsQueried={trace.bank_balance_chart.entity_ids_queried}
            />
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]">Net Assets chart</Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Excluded accounts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trace.net_assets_chart.series_trace.map((m) => (
                    <TableRow key={m.month}>
                      <TableCell className="text-xs">{m.month}</TableCell>
                      <TableCell className="text-xs">
                        {(m.source === 'no_coverage' || m.source === 'partial')
                          ? <span className="text-amber-700">{m.source}</span>
                          : m.source}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono">{m.net ?? '-'}</TableCell>
                      <TableCell className="text-xs text-amber-700">
                        {(m.excluded_accounts || []).length ? m.excluded_accounts.join(', ') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
